import { useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { easing } from 'maath';
import { Vector3 } from 'three';
import { CredentialRing } from './celestial/CredentialRing.jsx';
import { DustToText } from './celestial/DustToText.jsx';
import { GalaxyHero } from './celestial/GalaxyHero.jsx';
import { Moon } from './celestial/Moon.jsx';
import { NebulaVolume } from './celestial/NebulaVolume.jsx';
import { Planet } from './celestial/Planet.jsx';
import { SolarSystem } from './celestial/SolarSystem.jsx';
import { Star } from './celestial/Star.jsx';
import { VideoNebula } from './celestial/VideoNebula.jsx';
import { WarpStreaks } from './celestial/WarpStreaks.jsx';
import { CelestialBoundary } from './FlatScene.jsx';
import { computeJourneyPose, PIECES } from './journeyPath.js';
import { getScrollSnapshot } from '../scroll/scrollStore.js';

// One continuous world: every set piece exists along the path the whole time
// — the camera travels, so there is nothing to pop in. All smoothing is
// delta-time damped (research §5); the DOM tile layer reads the same pose
// math in JourneyMode.
export function JourneyScene() {
  const { camera } = useThree();
  const lookRef = useRef(new Vector3(0, 0.15, -7));
  const fovRef = useRef(48);
  const warpRef = useRef(0);
  const dustProgressRef = useRef(0);

  useFrame((_, dt) => {
    const { progress } = getScrollSnapshot();
    const pose = computeJourneyPose(progress);
    easing.damp3(camera.position, pose.pos, 0.18, dt);
    easing.damp3(lookRef.current, pose.look, 0.18, dt);
    fovRef.current += (pose.fov - fovRef.current) * (1 - Math.exp(-6 * dt));
    camera.fov = fovRef.current;
    camera.updateProjectionMatrix();
    camera.lookAt(lookRef.current);
    warpRef.current = pose.warp;
    dustProgressRef.current = pose.beat > 6 ? 1 : pose.beat === 6 ? pose.t : 0;
  });

  return (
    <>
      {/* 0 · hero galaxy */}
      <GalaxyHero position={PIECES.swirl} scale={1.4} />
      {/* 1 · about — Jupiter with companion moon */}
      <CelestialBoundary>
        <Planet
          map="/assets/textures/jupiter_2k.jpg"
          radius={1.15}
          position={PIECES.aboutPlanet}
          atmosphereColor="#d8b28a"
          atmosphereIntensity={0.5}
          spin={0.05}
        />
      </CelestialBoundary>
      {/* 2 · case 1 — rocky world we orbit, moon alongside */}
      <CelestialBoundary>
        <Planet
          map="/assets/textures/moon_2k.jpg"
          radius={1.15}
          position={PIECES.orbitPlanet}
          atmosphereColor="#8fa8c8"
          atmosphereIntensity={0.4}
          spin={0.03}
        >
          <Moon position={[1.9, 0.4, 0.6]} radius={0.3} />
        </Planet>
      </CelestialBoundary>
      {/* 3 · case 2 — the Grok nebula video we fly through, wrapped in a
          volumetric puff field (same sampled palette) so the passage has
          real parallax depth instead of a flat billboard */}
      <CelestialBoundary>
        <VideoNebula position={PIECES.nebula} width={16} />
        <NebulaVolume position={PIECES.nebula} />
      </CelestialBoundary>
      {/* 4 · experience — the star */}
      <Star position={PIECES.star} scale={1.5} />
      {/* 5+6 · credentials ring + proof dust-text standing in it */}
      <CelestialBoundary>
        <CredentialRing position={PIECES.ringPlanet} />
      </CelestialBoundary>
      <DustToText progressRef={dustProgressRef} position={PIECES.dustText} scale={0.6} />
      {/* 7 · the whole system */}
      <CelestialBoundary>
        <SolarSystem position={PIECES.system} scale={1.2} />
      </CelestialBoundary>
      <WarpStreaks warpRef={warpRef} />
    </>
  );
}
